import React, {ReactElement} from "react";

import {
  css,
  getContentRootPathBasedOnArticleType,
  localizeSlug,
  generateKeyId,
  getArticleBackBtnLabel,
  constructArticleTemplatePageURL,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {LOCALE} from "/src/config";
import {NO_CATEGORY_TYPES} from "/src/constants/common";

import SocialIcons from "../../social-icons/SocialIcons";
import Image, {ImageObject} from "../../image/Image";
import Video, {VideoType} from "../../videos/video/Video";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import Icon from "../../icon/Icon";
import LinkTag from "../../buttons/link-tag/LinkTag";
import {URLS} from "../../widgets/widget-share/WidgetShare";

import ChevronArrowIcon from "/src/svg/chevron-arrow.svg";

import * as styles from "./blog-hero.module.scss";

type HeroSideBySideModuleBlogPost =
  Queries.HeroSideBySideModuleFragmentFragment["blogPost"];

type BlogCategories = Pick<
  NonNullable<HeroSideBySideModuleBlogPost>,
  "categories"
>["categories"];

export type BlogAuthor = Pick<
  NonNullable<HeroSideBySideModuleBlogPost>,
  "author"
>["author"];

interface BlogHeroProps {
  author?: BlogAuthor;
  bgImage?: ImageObject;
  bgVideo?: string;
  wistiaBgVideo?: string;
  categories?: BlogCategories;
  slug?: string;
  cta?: CTA;
  eyebrow?: string;
  showSocialIcons?: boolean;
  showBackBtn?: boolean;
  title: string;
  type?: string;
  videoBehavior?: VideoType;
  videoControls?: boolean;
  copy?: string;
}

const BlogHero = (props: BlogHeroProps): ReactElement => {
  const {
    author,
    bgImage,
    bgVideo,
    wistiaBgVideo,
    categories,
    eyebrow,
    slug,
    title,
    videoBehavior,
    videoControls,
    type,
    cta,
    showSocialIcons,
    showBackBtn,
    copy,
  } = props;

  const goBackText = getArticleBackBtnLabel(type || "blog");
  const goBackUrl = getContentRootPathBasedOnArticleType(type || "blog");

  const tags =
    categories != null && categories.length ? (
      <div className={styles.blogHero__content__tags}>
        <p className={css("p-small", styles.blogHero__content__tagTitle)}>
          {translate("Tags:")}&nbsp;
        </p>
        <ul className={styles.blogHero__content__tagItems}>
          {(categories as BlogCategories)?.map((category, i) => {
            if (!category?.title) {
              return null;
            }

            const key = generateKeyId(`blog-hero-tags-${category.title}-$i`);

            let link = `${getContentRootPathBasedOnArticleType(
              type! || "blog"
            )}`;

            // glossary does not have cateogories page
            if (!NO_CATEGORY_TYPES.includes(type!)) {
              link = `${link}/category/${category.slug!}`;
            }

            return (
              <li key={key} className={styles.blogHero__content__tagItem}>
                {category.slug ? (
                  <LinkTag
                    classes={css("p-small", styles.blogHero__content__tagLink)}
                    href={link}
                  >
                    {category.title}
                  </LinkTag>
                ) : (
                  <span className={css("p-small")}>{category.title}</span>
                )}
                {i !== categories.length - 1 && <span>,&nbsp;</span>}
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.blogHero__videoBg)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoTitle={title!}
            type={VideoType.autoplay}
            videoSource={wistiaBgVideo}
            showControl={videoControls === true}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.blogHero__videoBg)}>
        <Video
          videoTitle={title!}
          type={videoBehavior}
          videoSource={bgVideo!}
          videoClasses={styles.blogHero__videoBg__video}
          showControl={videoControls === true}
        />
      </div>
    );
  };

  const pageLink = constructArticleTemplatePageURL(slug, type!);

  const hasBg = bgVideo || wistiaBgVideo || bgImage;

  return (
    <div className={css(styles.blogHero)}>
      {hasBg && (
        <div className={styles.blogHero__bgWrapper}>
          {!bgVideo && !wistiaBgVideo && bgImage && (
            <Image
              image={bgImage}
              objectFit="cover"
              classes={styles.blogHero__bgImg}
            />
          )}

          {constructVideoComponent()}

          <div className={styles.blogHero__overlay} />
        </div>
      )}

      <ModuleBody classes={styles.blogHero__body}>
        {showBackBtn && (
          <LinkTag
            href={goBackUrl}
            classes={css("p-small", styles.blogHero__backBtn)}
          >
            <Icon
              role="presentation"
              svg={ChevronArrowIcon}
              classes={styles.blogHero__backBtn__icon}
            />
            <span>{goBackText}</span>
          </LinkTag>
        )}
        <div className={styles.blogHero__content}>
          {eyebrow && (
            <p className={css("eyebrow", styles.blogHero__content__eyebrow)}>
              {eyebrow}
            </p>
          )}

          {title && (
            <h1 className={css("h1", styles.blogHero__content__title)}>
              {title}
            </h1>
          )}

          {tags}

          {copy && <p>{copy}</p>}

          {(author || showSocialIcons) && (
            <div className={styles.blogHero__content__socialWrapper}>
              {author && (
                <div className={styles.blogHero__content__author}>
                  {author.headshot && (
                    <Image
                      classes={styles.blogHero__content__authorImg}
                      image={author.headshot as ImageObject}
                    />
                  )}

                  <div
                    className={css(
                      styles.blogHero__content__authorMeta,
                      !author.headshot
                        ? styles.blogHero__content__authorMetaNoPad
                        : ""
                    )}
                  >
                    {author.name && <p className="p">{author.name}</p>}
                  </div>
                </div>
              )}
              {showSocialIcons && (
                <>
                  {author && author.name && (
                    <div className={styles.blogHero__content__socialsDivider} />
                  )}
                  <SocialIcons
                    id="blog-hero-social-icons"
                    iconList={[
                      {
                        icon: "twitter",
                        url: URLS.twitter(localizeSlug(pageLink, LOCALE)),
                      },
                      {
                        icon: "linkedin",
                        url: URLS.linkedin(localizeSlug(pageLink, LOCALE)),
                      },
                      {
                        icon: "facebook",
                        url: URLS.facebook(localizeSlug(pageLink, LOCALE)),
                      },
                    ]}
                    iconClasses={styles.blogHero__content__social}
                    wrapperClasses={styles.blogHero__content__socials}
                  />
                </>
              )}
            </div>
          )}

          {cta && (
            <div className={styles.blogHero__content__cta}>
              <CTAButton cta={cta} darkMode kind="primary" />
            </div>
          )}
        </div>
      </ModuleBody>
    </div>
  );
};

export default BlogHero;
